import isEqual from 'lodash/isEqual';
import { MutableRefObject, useCallback, useEffect, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import { AppDispatch } from '../../../store';
import { useProductList, useServiceList } from '../../../store/hooks/createReviewData';
import {
  fetchProductList,
  fetchServiceList,
  findAndSetOrganization,
  findAndSetService,
  setProduct,
  setProductList,
} from '../../../store/reducers/createReviewData';
import { fetchOrganizationList } from '../../../store/reducers/organizations';
import { fetchSravniRating } from '../../../store/reducers/ratings';
import { DEFAULT_FIELD_VALUES, REVIEW_FIELDS } from '../constants/common';
import { getChangedKeys } from '../helpers/getChangedKeys';
import { TFormInputs } from '../types';

import { useFieldChanged } from './useFieldChanged';

type TProps = {
  isRestoring: MutableRefObject<boolean>;
  isEdit: boolean;
  storedReview: Partial<TFormInputs>;
  setStoredReview: (value: Partial<TFormInputs>) => void;
  onChange: (values: TFormInputs, changedKeys: Array<keyof TFormInputs>) => void;
};

export const useHandleChanges = (props: TProps) => {
  const { isRestoring, isEdit, storedReview, setStoredReview, onChange } = props;
  const { control, setValue, getValues, clearErrors, reset } = useFormContext<TFormInputs>();

  const values = useWatch<TFormInputs>({ control });

  const prevValues = useRef<Partial<TFormInputs>>();
  const dispatch = useDispatch<AppDispatch>();
  const serviceList = useServiceList();
  const productList = useProductList();

  const getNewProducts = useCallback(
    (values: TFormInputs) => {
      const organizationType = values[REVIEW_FIELDS.ORGANIZATION_TYPE];
      const organizationId = values[REVIEW_FIELDS.ORGANIZATION_ID];
      const service = values[REVIEW_FIELDS.SERVICE];

      if (!organizationType || !organizationId || !service) {
        setProductList(null);
        setValue(REVIEW_FIELDS.PRODUCT_ID, null);
        return;
      }

      clearErrors([REVIEW_FIELDS.PRODUCT_ID]);

      dispatch(
        fetchProductList({
          service,
          orgType: organizationType,
          orgId: organizationId,
        }),
      );
    },
    [clearErrors, dispatch, setValue],
  );

  const formInitialization = useCallback(
    async (values: TFormInputs) => {
      const {
        [REVIEW_FIELDS.ORGANIZATION_TYPE]: organizationType,
        [REVIEW_FIELDS.ORGANIZATION_ID]: organizationId,
        [REVIEW_FIELDS.SERVICE]: service,
      } = values;

      if (!organizationType) return;

      const promises = [
        dispatch(fetchOrganizationList(organizationType)),
        dispatch(fetchServiceList(organizationType)),
      ];

      await Promise.all(promises);

      dispatch(findAndSetOrganization(organizationId));
      dispatch(findAndSetService(service));

      getNewProducts(values);
    },
    [dispatch, getNewProducts],
  );

  const onChangeService = ({ value: service, allValues }) => {
    if (!serviceList) return;

    dispatch(findAndSetService(service));
    getNewProducts(allValues);
  };

  const onChangeOrganizationId = ({ value: organizationId, allValues }) => {
    dispatch(findAndSetOrganization(organizationId));
    getNewProducts(allValues);
  };

  useEffect(() => {
    if (isEqual(values, prevValues.current)) return;

    if (isRestoring.current) {
      const changedKeys = getChangedKeys<TFormInputs>(values, storedReview);

      prevValues.current = storedReview;
      reset(storedReview, { keepDefaultValues: true });

      onChange?.(storedReview as TFormInputs, changedKeys);
      formInitialization(storedReview as TFormInputs);
      isRestoring.current = false;
      return;
    }

    const changedKeys = getChangedKeys<TFormInputs>(values, prevValues.current);

    if (isEdit) {
      onChange?.(values as TFormInputs, changedKeys);
      return;
    }

    const isUserWantToSubmit =
      changedKeys.length === 1 &&
      changedKeys[0] === REVIEW_FIELDS.IS_USER_READY_TO_SUBMIT &&
      values[REVIEW_FIELDS.IS_USER_READY_TO_SUBMIT] === true;

    const newFormValues: Partial<TFormInputs> = {
      ...(values as Partial<TFormInputs>),
      ...(!isUserWantToSubmit && { [REVIEW_FIELDS.IS_USER_READY_TO_SUBMIT]: false }),
    };

    const isTextExist =
      newFormValues[REVIEW_FIELDS.TEXT] &&
      newFormValues[REVIEW_FIELDS.TEXT] !== DEFAULT_FIELD_VALUES[REVIEW_FIELDS.TEXT];
    const isTitleExist =
      newFormValues[REVIEW_FIELDS.TITLE] &&
      newFormValues[REVIEW_FIELDS.TITLE] !== DEFAULT_FIELD_VALUES[REVIEW_FIELDS.TITLE];
    const isTextOrTitleExist = isTextExist || isTitleExist;

    if (isTextOrTitleExist) {
      setStoredReview(newFormValues);
    } else {
      setStoredReview(null);
    }

    !isUserWantToSubmit && setValue(REVIEW_FIELDS.IS_USER_READY_TO_SUBMIT, false);
    onChange?.(newFormValues as TFormInputs, changedKeys);
    prevValues.current = values as Partial<TFormInputs>;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onChange, values]);

  useEffect(() => {
    // При инициализации формы
    if (isRestoring.current) return;

    const allValues = getValues();
    formInitialization(allValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChangeOrganizationType = ({ value: organizationType }) => {
    clearErrors([REVIEW_FIELDS.ORGANIZATION_ID, REVIEW_FIELDS.SERVICE, REVIEW_FIELDS.PRODUCT_ID]);

    if (!organizationType) return;

    dispatch(fetchServiceList(organizationType));
    dispatch(fetchOrganizationList(organizationType));
    dispatch(fetchSravniRating(organizationType));
  };

  const onChangeProductId = ({ value: productId }) => {
    const newProduct = productList?.find((product) => product.id === productId);
    dispatch(setProduct(newProduct));
  };

  useFieldChanged({
    name: REVIEW_FIELDS.ORGANIZATION_ID,
    control,
    getValues,
    onChange: onChangeOrganizationId,
  });

  useFieldChanged({
    name: REVIEW_FIELDS.ORGANIZATION_TYPE,
    control,
    getValues,
    onChange: onChangeOrganizationType,
  });

  useFieldChanged({
    name: REVIEW_FIELDS.SERVICE,
    control,
    getValues,
    onChange: onChangeService,
  });

  useFieldChanged({
    name: REVIEW_FIELDS.PRODUCT_ID,
    control,
    getValues,
    onChange: onChangeProductId,
  });
};
